import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export function RecommendedPosts() {
  const { slug } = useParams<{ slug: string }>();
  const [location] = useLocation();
  
  // Check if we're on a blog post page to pass the current post as context
  const inBlogPostPage = location.startsWith('/blog/') && slug;
  
  const { data, isLoading } = useQuery({
    queryKey: ['/api/blog/recommendations', slug],
    queryFn: () => {
      if (inBlogPostPage) {
        return getQueryFn({ on401: "throw" })(`/api/blog/recommendations?slug=${slug}`);
      }
      return getQueryFn({ on401: "throw" })('/api/blog/recommendations');
    },
    enabled: true
  });
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-semibold mb-4">Recommended For You</h3>
        <div className="space-y-4">
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
        </div>
      </div>
    );
  }
  
  const recommendedPosts = data?.posts || [];
  
  if (recommendedPosts.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold mb-4">Recommended For You</h3>
      <div className="space-y-4">
        {recommendedPosts.map((post) => (
          <Card key={post.id} className="overflow-hidden">
            <CardContent className="p-4">
              <Badge variant="outline" className="mb-2">
                {post.category}
              </Badge>
              <h4 className="font-medium line-clamp-2 mb-2">
                {post.title}
              </h4>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {post.summary}
              </p>
            </CardContent>
            <CardFooter className="pt-0 px-4 pb-4">
              <Button asChild variant="ghost" size="sm" className="ml-auto">
                <a href={`/blog/${post.slug}`}>
                  Read <ChevronRight className="h-3 w-3 ml-1" />
                </a>
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="space-y-3 p-4 border rounded-lg">
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-4 w-24 ml-auto" />
    </div>
  );
}